import axios from 'axios';

import { responseParser } from './responseParser';
import { bridge } from '@wwe-portal/web/libs/bridge';
import { API_ENDPOINT_NEST } from '@wwe-portal/web/utils/config';
import { authStore } from '@wwe-portal/web/providers/stores/auth-store';
import { toast } from '@wwe-portal/ui/components/toast';
import { getTrackerInstance } from '@wwe-portal/web/libs/analytics/titan';
import {
  APIError,
  RequestCancelError,
  UnauthorizedError,
  UnexpectedError,
} from './error';

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  //   'Cache-Control': 'no-cache, no-store, must-revalidate',
};

const retryAxios = axios.create();
retryAxios.interceptors.response.use(responseParser);

let isRefreshing = false;
// failed(403) requests
let failedQueue: {
  resolve: (value: unknown) => void;
  reject: (reason?: any) => void;
}[] = [] as any;
let retry = false;
const processQueue = (error: any) => {
  failedQueue.forEach((prom: any) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

const parseError = (error: unknown) => {
  if (axios.isCancel(error)) {
    return new RequestCancelError(error);
  }
  if (!axios.isAxiosError(error)) return new UnexpectedError(error);
  if (error.response?.status === 401 || error.response?.status === 403) {
    return new UnauthorizedError(error);
  }
  if (typeof error.response?.data?.error === 'number') {
    return new APIError(error.response?.data?.error, error);
  }
  return new UnexpectedError(error);
};

const getOriginConfig = (rawError: unknown) => {
  if (axios.isAxiosError(rawError)) {
    return rawError.config;
  }

  return null;
};

export const responseInterceptorOnErrorV2 = async (rawError: unknown) => {
  const error = parseError(rawError);
  const originalRequest = getOriginConfig(rawError);
  const token = bridge.getRefreshToken();

  if (error instanceof UnauthorizedError && originalRequest != null) {
    if (!retry) {
      if (isRefreshing) {
        // If I'm refreshing the token I send request to a queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return retryAxios(originalRequest);
          })
          .catch((err) => err);
      }

      retry = true;
      isRefreshing = true; // set the refreshing var to true

      if (!token) {
        failedQueue = [];
        authStore.getState().logout(false);
        throw error;
      }

      // If none of the above, refresh the token and process the queue
      return new Promise((resolve, reject) => {
        refreshAccessToken() // The method that refreshes my token
          .then(() => {
            processQueue(null); // Resolve queued
            resolve(retryAxios(originalRequest)); // Resolve current

            isRefreshing = false;
            retry = false;
          })
          .catch((err) => {
            failedQueue = [];
            reject(err);
            authStore.getState().logout(false);
          });
      });
    }
  }

  throw error;
};

const axiosInstance = axios.create({
  baseURL: API_ENDPOINT_NEST,
});

export const refreshAccessToken = async () => {
  try {
    const token = bridge.getRefreshToken();
    const { data } = await axiosInstance(
      `${API_ENDPOINT_NEST}/auth/v2/token/refresh`,
      {
        headers: defaultHeaders,
        method: 'POST',
        withCredentials: true,
        data: {
          token,
          deviceToken: getTrackerInstance().tracker.getDeviceToken(),
        },
      }
    );

    bridge.saveRefreshToken(data?.data?.refreshToken);

    return data.statusCode === 204 || data.statusCode === 200;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 403) {
      toast({
        content: 'Your session has expired. Please refresh.',
        variant: 'error',
      });
    }

    authStore.getState().logout(true);
  }
};
